import { useEffect, useState } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { getNeighbourhoods, trackEvent } from '../lib/api';
import SEO from '../components/SEO';

export default function NeighbourhoodsPage() {
  const [neighbourhoods, setNeighbourhoods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'neighbourhoods' });
    getNeighbourhoods().then(r => setNeighbourhoods(r.data.neighbourhoods || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="neighbourhoods-page">
      <SEO
        title="Burlington Neighbourhood Guides — Pricing, Schools & Commute by Area"
        description="Compare all 21 Burlington, Ontario neighbourhoods. Pricing from $380K to $3.5M, school catchments, commute times, and lifestyle — structured for homebuyers."
        canonical="/neighbourhoods"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Neighbourhoods', url: '/neighbourhoods' }]}
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/nfduamj0_shutterstock_1584051751.jpg" alt="Burlington neighbourhoods aerial view" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Burlington, Ontario</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Neighbourhood <span className="italic">guides</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
            Eleven distinct areas, each with unique character. Pricing, schools, commute, lifestyle — structured for quick comparison.
          </p>
          <Link to="/blueprint" data-testid="neighbourhoods-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
              Find Your Best Fit <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="rounded-2xl border border-slate-200/80 p-6 animate-pulse">
                  <div className="h-4 bg-slate-100 rounded-full w-2/3 mb-3"></div>
                  <div className="h-3 bg-slate-50 rounded-full w-full mb-2"></div>
                  <div className="h-3 bg-slate-50 rounded-full w-4/5"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {neighbourhoods.map((n) => (
                <Link
                  key={n.slug}
                  to={`/neighbourhoods/${n.slug}`}
                  className="group bg-white rounded-2xl p-6 md:p-8 card-interactive"
                  data-testid={`neighbourhood-link-${n.slug}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                    <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">Burlington</span>
                  </div>
                  <h3 className="text-xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    {n.name}
                  </h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed mb-5">{n.vibe_summary}</p>

                  <div className="space-y-1.5 pt-4 border-t border-slate-100 mb-4">
                    {n.price_bands?.detached?.min > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Detached</span>
                        <span className="font-mono text-slate-700">{formatPriceRange(n.price_bands.detached.min, n.price_bands.detached.max)}</span>
                      </div>
                    )}
                    {n.price_bands?.town?.min > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Town</span>
                        <span className="font-mono text-slate-700">{formatPriceRange(n.price_bands.town.min, n.price_bands.town.max)}</span>
                      </div>
                    )}
                    {n.price_bands?.condo?.min > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Condo</span>
                        <span className="font-mono text-slate-700">{formatPriceRange(n.price_bands.condo.min, n.price_bands.condo.max)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {n.tags?.slice(0, 4).map(tag => (
                      <span key={tag} className="text-[10px] font-mono bg-slate-50 text-slate-500 px-2 py-0.5 rounded-full">{tag.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center">
            <div className="absolute inset-0">
              <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/yr1apojk_shutterstock_2665884737.jpg" alt="Burlington waterfront pier" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/75" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Not sure which area <span className="italic">fits?</span>
              </h2>
              <p className="text-sm text-white/40 mb-6 max-w-md mx-auto">
                Take the Buyer Blueprint — 6 structured questions, instant neighbourhood matches scored to your priorities.
              </p>
              <Link to="/blueprint" data-testid="neighbourhoods-bottom-cta">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all">
                  Start Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
